import { t } from "@lingui/core/macro";
import * as Square from "@square/web-sdk";
import { useRef, useState } from "react";

import { Button } from "@whoosh/design-system/src/components/controls";
import { useModalTransition } from "@whoosh/design-system/src/components/overlay/Modal/ModalTransitionContext";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { SquareCardEntry } from "@whoosh/features/src/Square";

import { CardEntryErrorBanner } from "../CardEntryErrorBanner";
import { SquareCardEntryModalProps } from "../types";
import { cardSaveRejected, getCardEntryError } from "./cardEntryErrors";

export const SquareCardEntryModal = ({
  isOpen,
  onCardEntryCancel,
  onCardEntryError,
  onCardEntryComplete,
  onCardEntrySaved,
  loading,
  setLoading,
}: SquareCardEntryModalProps) => {
  const { runAfterIdle } = useModalTransition();
  const [card, setCard] = useState<Square.Card>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);
  const submittingRef = useRef(false);

  const close = (onClosed: () => void) => {
    setClosing(true);
    void card?.detach();
    setCard(undefined);
    setLoading(false);
    runAfterIdle(onClosed);
  };

  const submit = async () => {
    if (!card || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setErrorMessage(undefined);
    let saving = false;
    try {
      const response = await card.tokenize();
      const details = response.details?.card;
      if (!response.token || !details) {
        setErrorMessage(cardSaveRejected().message);
        return;
      }
      saving = true;
      const cardId = await onCardEntryComplete({
        nonce: response.token,
        card: {
          brand: details.brand,
          expirationMonth: details.expMonth,
          expirationYear: details.expYear,
          lastFourDigits: details.last4,
          postalCode: response.details?.billing?.postalCode ?? "",
          type: details.cardType ?? "",
          prepaidType: "",
        },
      });
      close(() => onCardEntrySaved(cardId));
    } catch (error: unknown) {
      const failure = saving ? getCardEntryError(error) : cardSaveRejected();
      if (failure.canRetryInForm) {
        setErrorMessage(failure.message);
      } else {
        close(() => onCardEntryError(failure.message));
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <StandardModal
      isOpen={isOpen && !closing}
      onClose={() => {
        if (!closing && !submittingRef.current) close(onCardEntryCancel);
      }}
      hideCloseButton={submitting}
      upperTitle={t`Add a card`}
      content={
        <Box flex={1} px={6} pb={6}>
          <CardEntryErrorBanner message={errorMessage} />
          {isOpen && !closing ? (
            <SquareCardEntry
              card={card}
              setCard={setCard}
              loading={loading || submitting}
              setLoading={setLoading}
            />
          ) : null}
          <Button
            onPress={() => void submit()}
            isDisabled={!card || loading || submitting}
            isLoading={submitting}
            accessibilityLabel={t`Add Credit Card button`}
            testID="add-credit-card-button"
          >{t`Add Credit Card`}</Button>
        </Box>
      }
    />
  );
};
